import React, { useCallback, useContext, useMemo, useState } from 'react';
import { AuthContext } from 'contexts';
import { type RouteComponentProps } from 'react-router-dom';
import {
  Color,
  Flex,
  message,
  Title,
  HPanel,
  PanelBody,
  PanelHeading,
} from '@bb/elements-web';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getAppointments,
  getCentralizedQueuePreferencesState,
  getMlsTypeFilterOptions,
  getQueuePreferences,
} from 'api/AnsweringOrgController';
import type { GetAppointmentsResponse } from 'api/AnsweringOrgController/getAppointments';
import { MLS_TYPE_LABEL_MAP } from 'components/UniversalSearch/constants';
import type { FilteredMlsOptionsType } from 'components/UniversalSearch/types';
import { useAppointmentWatch, useDebounce } from 'hooks';
import styled from 'styled-components';
import type { EnhancedAppointment } from '@bb/domain/dist/types';
import useViewDashAppointment from 'hooks/useViewDashAppointment';
import { orderBy } from 'lodash';
import QueueToolbar from './QueueToolbar';
import QueueList from './QueueList';
import type { SortColumn, SortDirection } from './QueueList';
import QueuePreferencesModal from './QueuePreferencesModal';
import type { AppointmentStatusEventEntry } from './types';
import {
  applyFlaggedUpdateToAnsweringCache,
  applyStatusUpdateToAnsweringCache,
  getUrgency,
} from './utils';

const QUEUE_PAGE_SIZE = 20;

const SORT_KEY_MAP: Record<SortColumn, string> = {
  address: 'appointment.listing.line1',
  agent: 'appointment.coop.name',
  status: 'appointment.status',
  date: 'appointment.time',
};

const AnsweringQueue: React.FC<RouteComponentProps> = ({
  location,
  history,
}) => {
  // Toolbar state
  const [isMlsFilterVisible, setIsMlsFilterVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 350);
  // Queue Preferences Modal State
  const [isPrefsModalOpen, setIsPrefsModalOpen] = useState(false);
  // Queue List State
  const [selectedRows, setSelectedRows] = useState<EnhancedAppointment[]>([]);
  const [sortColumn, setSortColumn] = useState<SortColumn>(
    () =>
      (sessionStorage.getItem('answeringQueueSortColumn') as SortColumn) ||
      'date',
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    () =>
      (sessionStorage.getItem(
        'answeringQueueSortDirection',
      ) as SortDirection) || 'asc',
  );

  const queryClient = useQueryClient();
  const { auth, pusher } = useContext(AuthContext);
  const hasApptFeature = auth?.org?.features.includes('APPOINTMENT');

  const viewDashAppointment = useViewDashAppointment();

  const { data: queuePreferences } = useQuery({
    queryKey: ['queuePreferences'],
    queryFn: getQueuePreferences,
  });

  const onStatusChange = useCallback(
    async (entry: AppointmentStatusEventEntry) => {
      if (!entry?.appointmentId || !entry?.appointment) return;

      queryClient.setQueriesData({ queryKey: ['answeringAppointments'] }, old =>
        applyStatusUpdateToAnsweringCache(old, entry, queuePreferences),
      );
    },
    [queryClient, queuePreferences],
  );

  const onFlagged = useCallback(
    (args: { appointmentId: string; flagged: boolean }) => {
      queryClient.setQueriesData({ queryKey: ['answeringAppointments'] }, old =>
        applyFlaggedUpdateToAnsweringCache(old, args),
      );
    },
    [queryClient],
  );

  const onAnsweringSettingsToggled = useCallback(() => {
    // refetch appointments with new settings
    queryClient.invalidateQueries({ queryKey: ['answeringAppointments'] });
    message.info('Answering settings updated, refreshing appointments');
  }, [queryClient]);

  const { viewingState, viewAppointment } = useAppointmentWatch(
    pusher,
    auth?.org?.id,
    auth?.org?.type,
    hasApptFeature,
    { onStatusChange, onFlagged, onAnsweringSettingsToggled },
  );

  const { data: mlsOptions = [] } = useQuery({
    queryKey: ['mlsOptions'],
    queryFn: getMlsTypeFilterOptions,
    select: options =>
      options.reduce((acc, curr) => {
        acc.push({
          label: MLS_TYPE_LABEL_MAP[curr] || curr,
          value: curr,
        });
        return acc;
      }, []) satisfies { label: string; value: string }[],
    keepPreviousData: true,
  });

  const filteredMlsOptions: FilteredMlsOptionsType = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const selectedMlsParams =
      params.get('selectedMls')?.split(',').filter(Boolean) || [];

    if (!mlsOptions || !mlsOptions.length) {
      return selectedMlsParams.reduce(
        (acc: Record<string, boolean>, value: string) => {
          acc[value] = true;
          return acc;
        },
        {},
      );
    }

    const filteredOpts = mlsOptions.reduce((acc, { value }) => {
      acc[value] = false;
      return acc;
    }, {});

    selectedMlsParams?.forEach((mls: string) => {
      if (filteredOpts[mls] !== undefined) {
        filteredOpts[mls] = true;
      }
    });
    return filteredOpts;
  }, [mlsOptions, location.search]);

  const setFilteredMlsOptions = useCallback(
    (newFilteredMlsOptions: FilteredMlsOptionsType) => {
      const selectedMls = Object.keys(newFilteredMlsOptions).filter(
        key => newFilteredMlsOptions[key],
      );
      const params = new URLSearchParams(location.search);
      if (selectedMls.length) {
        params.set('selectedMls', selectedMls.join(','));
      } else {
        params.delete('selectedMls');
      }
      const newSearch = params.toString();
      history.replace({
        ...location,
        search: newSearch ? `?${newSearch}` : '',
      });
    },
    [location, history],
  );

  const filteredMlsOptionsList = useMemo(
    () =>
      Object.keys(filteredMlsOptions).filter(key => filteredMlsOptions[key]),
    [filteredMlsOptions],
  );

  const {
    data: appointmentsInfiniteData,
    isLoading: isLoadingAppointments,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching: isFetchingAppointments,
  } = useInfiniteQuery<GetAppointmentsResponse>({
    queryKey: [
      'answeringAppointments',
      debouncedSearchText,
      filteredMlsOptionsList,
    ],
    queryFn: ({ pageParam = 0 }) =>
      getAppointments({
        page: pageParam as number,
        search: debouncedSearchText,
        mlsTypes: filteredMlsOptionsList,
        limit: QUEUE_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.data.length === 0) return undefined;
      const totalLoaded = allPages.reduce((sum, p) => sum + p.data.length, 0);
      if (totalLoaded >= lastPage.maxCount) return undefined;
      return allPages.length;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['centralizedQueuePreferencesState'],
      });
    },
  });

  const { data: preferencesState } = useQuery({
    queryKey: ['centralizedQueuePreferencesState'],
    queryFn: getCentralizedQueuePreferencesState,
  });

  const flatData = useMemo(
    () => appointmentsInfiniteData?.pages?.flatMap(p => p.data) ?? [],
    [appointmentsInfiniteData?.pages],
  );

  const appointmentData = useMemo(() => {
    if (!flatData.length) return [];
    return orderBy(
      flatData.map(({ appointment, listing }) => ({
        appointment: {
          ...appointment,
          urgency: getUrgency(appointment),
        },
        listing,
      })),
      ['appointment.urgency', SORT_KEY_MAP[sortColumn]],
      ['asc', sortDirection],
    );
  }, [flatData, sortColumn, sortDirection]);

  const handleSort = useCallback(
    (column: SortColumn) => {
      if (column === sortColumn) {
        const newDir = sortDirection === 'asc' ? 'desc' : 'asc';
        setSortDirection(newDir);
        sessionStorage.setItem('answeringQueueSortDirection', newDir);
      } else {
        setSortColumn(column);
        setSortDirection('asc');
        sessionStorage.setItem('answeringQueueSortColumn', column);
        sessionStorage.setItem('answeringQueueSortDirection', 'asc');
      }
    },
    [sortColumn, sortDirection],
  );

  const handleServiceMultipleClick = useCallback(() => {
    if (!selectedRows.length) return;
    const firstAppointment = selectedRows[0];
    const sortedIds = selectedRows.map(r => r.id);
    viewDashAppointment(firstAppointment, { sids: sortedIds });
  }, [selectedRows, viewDashAppointment]);

  const handleToggleSelectedRow = useCallback(
    (appointment: EnhancedAppointment) => {
      if (!appointment?.id) return;
      setSelectedRows(prev => {
        const exists = prev.some(app => app.id === appointment.id);
        if (exists) {
          return prev.filter(app => app.id !== appointment.id);
        }
        return [...prev, appointment];
      });
    },
    [],
  );

  const handleOpenPrefsModal = useCallback(() => {
    setIsMlsFilterVisible(false);
    setIsPrefsModalOpen(true);
  }, []);

  const handleToggleSelectAllRows = useCallback(
    (selectAll: boolean) => {
      setSelectedRows(
        selectAll ? appointmentData.map(curr => curr.appointment) : [],
      );
    },
    [appointmentData],
  );

  const handlePrefsModalClose = useCallback(
    () => setIsPrefsModalOpen(false),
    [],
  );

  return (
    <>
      <HPanel condensed>
        <PanelHeading built>
          <Flex justify="space-between" align="center" wrap="wrap">
            <StyledTitle
              dataCy="answering-queue-title"
              level={2}
              margin="0 0 5px"
            >
              Central Showing Queue
            </StyledTitle>
            <QueueToolbar
              mlsOptions={mlsOptions}
              isMlsFilterVisible={isMlsFilterVisible}
              setIsMlsFilterVisible={setIsMlsFilterVisible}
              filteredMlsOptions={filteredMlsOptions}
              onSetFilteredMlsOptions={setFilteredMlsOptions}
              serviceMultipleCount={selectedRows.length}
              isPreferencesModified={preferencesState?.isModified || false}
              onServiceMultipleClick={handleServiceMultipleClick}
              onOpenPrefsModal={handleOpenPrefsModal}
              searchText={searchText}
              isLoadingSearch={!!debouncedSearchText && isFetchingAppointments}
              onSearch={setSearchText}
            />
          </Flex>
        </PanelHeading>
      </HPanel>
      <HPanel>
        <PanelBody>
          <QueueList
            data={appointmentData}
            isLoadingData={isLoadingAppointments}
            hasMore={!!hasNextPage}
            isLoadingMore={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            selectedRows={selectedRows}
            viewingState={viewingState}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            onToggleSelectedRow={handleToggleSelectedRow}
            onToggleSelectAllRows={handleToggleSelectAllRows}
            onViewAppointment={viewAppointment}
            onFilter={setSearchText}
          />
        </PanelBody>
      </HPanel>
      <QueuePreferencesModal
        open={isPrefsModalOpen}
        onClose={handlePrefsModalClose}
      />
    </>
  );
};

const StyledTitle = styled(Title)`
  &&& {
    color: ${Color.TERTIARY_TEXT};
    font-weight: 300;
  }
`;

export default AnsweringQueue;
